import { motion } from 'motion/react';

import { AppLogo } from '@/assets/icons/AppLogo';
import { Icons } from '@/assets/icons/icons';
import { useLogoFlutter } from '@/assets/icons/useLogoFlutter';
import { Button } from '@/components/ui/Button';
import { TextInput } from '@/components/ui/form/input';
import { OTPCodeInput } from '@/components/ui/OTPCodeInput';
import { Separator } from '@/components/ui/Separator';
import { getSniperLink } from '@/features/login/sniper-link';
import { cn } from '@/lib/utils';
import { handleExternalUrlClick, handleMailTo } from '@/utils/platform';

import {
  isValidEmail,
  trackEmailUpdateEvent,
  trackLoginCodeUpdateEvent,
  trackSniperLinkClicked,
} from './auth-tracking';
import { MEADOW_SCENE } from './meadow-scene';

import type { ReactNode } from 'react';
import './meadow-login.css';

/**
 * "Meadow" login design (Option G from the design handoff): the same sign-in
 * card, centered on a painterly scenic backdrop. These are purely presentational
 * — all auth logic, state and analytics events live in the container screens
 * (AuthForm / DesktopLoginScreen). Input-change tracking is shared with the
 * other auth screens via auth-tracking.
 */

export const MeadowScene = ({ children }: { children: ReactNode }) => {
  return (
    <div className="meadow-scope meadow-scene relative h-dvh w-screen overflow-hidden bg-[var(--meadow-card)] md:bg-[#1B1C15]">
      <img
        src={MEADOW_SCENE.imageUrl}
        alt=""
        aria-hidden
        className="absolute inset-0 hidden h-full w-full object-cover md:block"
        style={{ objectPosition: MEADOW_SCENE.objectPosition }}
      />
      <div
        className="absolute inset-0 hidden md:block"
        style={{ background: MEADOW_SCENE.wash }}
      />

      <div className="relative z-10 flex h-full justify-center overflow-y-auto md:items-center md:p-10">
        <div
          className={cn(
            'flex min-h-full w-full flex-col bg-[var(--meadow-card)]',
            'px-6 pb-[max(28px,env(safe-area-inset-bottom))] pt-[max(24px,env(safe-area-inset-top))]',
            'md:min-h-[640px] md:w-[420px] md:rounded-[20px] md:border md:border-[var(--meadow-card-border)] md:px-8 md:pb-7 md:pt-9'
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export const MeadowHeader = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => {
  const { bodyY, wingAngle, playFlutter } = useLogoFlutter();

  return (
    <div className="mb-10 flex flex-col items-center gap-5 text-center">
      <motion.div
        onMouseEnter={playFlutter}
        style={{
          y: bodyY,
          display: 'inline-flex',
          overflow: 'visible',
          willChange: 'transform',
        }}
      >
        <AppLogo size={42} wingAngle={wingAngle} />
      </motion.div>
      <div className="flex flex-col gap-2.5">
        <h1 className="font-serif text-3xl leading-tight tracking-tight text-foreground">
          {title}
        </h1>
        <p className="text-[14px] leading-normal text-foreground-subtle dark:text-foreground-muted">
          {description}
        </p>
      </div>
    </div>
  );
};

const ssoButtonClass = cn(
  'flex h-12 w-full items-center justify-center gap-2.5 rounded-lg',
  'border border-border-strong bg-surface text-[15px] font-medium text-foreground',
  'transition-colors focus-ring hover:border-border-strongest hover:bg-surface-hover',
  'disabled:cursor-not-allowed disabled:opacity-50'
);

const ssoIconFrameClass =
  'grid size-[22px] shrink-0 place-items-center [&>div]:contents [&_svg]:block';

// Trims the label's box to cap-height/baseline so flex centering aligns the
// actual letterforms; without it the label sits visibly lower in Safari than
// in Chrome.
const ssoLabelClass = '[text-box:trim-both_cap_alphabetic]';

export const MeadowLegal = () => (
  <p className="mt-7 text-balance text-center text-xs leading-5 text-foreground-subtle">
    By continuing you agree to our{' '}
    <a
      className="rounded-sm font-medium text-foreground underline underline-offset-2 focus-ring"
      href="https://littlebird.ai/terms-of-use"
      target="_blank"
      rel="noopener noreferrer"
    >
      Terms
    </a>{' '}
    &amp; have read our{' '}
    <a
      className="rounded-sm font-medium text-foreground underline underline-offset-2 focus-ring"
      href="https://littlebird.ai/privacy-policy"
      target="_blank"
      rel="noopener noreferrer"
    >
      Privacy Policy
    </a>
    .
  </p>
);

const MeadowSupport = () => (
  <p className="mt-3 text-center text-xs text-foreground-subtle">
    Having issues?{' '}
    <button
      type="button"
      className="rounded-sm font-medium text-foreground underline-offset-2 focus-ring hover:underline"
      onClick={() =>
        handleMailTo(
          'support@littlebird.ai',
          'I need assistance',
          "I'm unable to login"
        )
      }
    >
      Contact Support
    </button>
  </p>
);

type MeadowEmailStepProps = {
  email: string;
  setEmail: (email: string) => void;
  onSubmit: () => void;
  onGoogleLogin: () => void;
  onAppleLogin?: () => void;
  onMicrosoftLogin?: () => void;
  isProcessing: boolean;
  lastUsedEmail?: string | null;
};

const MeadowEmailStep = ({
  email,
  setEmail,
  onSubmit,
  onGoogleLogin,
  onAppleLogin,
  onMicrosoftLogin,
  isProcessing,
  lastUsedEmail,
}: MeadowEmailStepProps) => {
  const canSubmit = isValidEmail(email) && !isProcessing;

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex flex-1 flex-col justify-center">
        <MeadowHeader
          title="Welcome to Littlebird"
          description="Sign in or create an account to get started."
        />
      </div>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={onGoogleLogin}
          disabled={isProcessing}
          className={ssoButtonClass}
        >
          <span className={ssoIconFrameClass}>
            <Icons.google className="size-[17px] -translate-y-px" />
          </span>
          <span className={ssoLabelClass}>Continue with Google</span>
        </button>
        {onAppleLogin && (
          <button
            type="button"
            onClick={onAppleLogin}
            disabled={isProcessing}
            className={ssoButtonClass}
          >
            <span className={ssoIconFrameClass}>
              {/* Lifted so the apple body (not its bounding box, which
                  includes the leaf) is optically centered on the label. */}
              <Icons.apple className="size-[21px] -translate-y-[2px]" />
            </span>
            <span className={ssoLabelClass}>Continue with Apple</span>
          </button>
        )}
        {onMicrosoftLogin && (
          <button
            type="button"
            onClick={onMicrosoftLogin}
            disabled={isProcessing}
            className={ssoButtonClass}
          >
            <span className={ssoIconFrameClass}>
              <Icons.microsoft className="size-[17px]" />
            </span>
            <span className={ssoLabelClass}>Continue with Microsoft</span>
          </button>
        )}
      </div>

      <div className="my-5 flex items-center gap-4">
        <Separator className="flex-1" />
        <span className="text-sm text-foreground-subtle">or</span>
        <Separator className="flex-1" />
      </div>

      <div className="flex flex-col gap-3">
        <TextInput
          id="email"
          value={email}
          placeholder="you@littlebird.ai"
          className="h-12 text-[15px]"
          type="email"
          autoComplete="email"
          disabled={isProcessing}
          onChange={(e) => {
            setEmail(e.currentTarget.value.toLowerCase());
            trackEmailUpdateEvent(e.currentTarget.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (canSubmit) onSubmit();
            }
          }}
        />
        <Button
          className="h-12 w-full focus-ring"
          type="submit"
          font="sans"
          size="lg"
          disabled={!canSubmit}
          isLoading={isProcessing}
          onClick={onSubmit}
        >
          Continue with email
        </Button>
      </div>

      {lastUsedEmail && (
        <p className="mt-3 text-center text-xs text-foreground-subtle">
          Last signed in as {lastUsedEmail}
        </p>
      )}

      <MeadowLegal />
      <MeadowSupport />
    </div>
  );
};

const MeadowBackButton = ({
  onBack,
  disabled,
}: {
  onBack: () => void;
  disabled: boolean;
}) => (
  <Button
    variant="ghost"
    size="sm"
    className="flex items-center gap-1 self-start focus-ring"
    onClick={onBack}
    disabled={disabled}
  >
    <Icons.chevronLeft className="size-4" />
    <span className="text-sm">Back</span>
  </Button>
);

type MeadowSsoStepProps = {
  email: string;
  onContinue: () => void;
  onUseCode: () => void;
  onBack: () => void;
  isProcessing: boolean;
};

const MeadowSsoStep = ({
  email,
  onContinue,
  onUseCode,
  onBack,
  isProcessing,
}: MeadowSsoStepProps) => (
  <div className="flex flex-1 flex-col">
    <MeadowBackButton onBack={onBack} disabled={isProcessing} />

    <div className="flex flex-1 flex-col justify-center">
      <MeadowHeader
        title="Sign in with SSO"
        description={`Your company uses single sign-on for ${email}`}
      />

      <div className="flex flex-col items-center gap-4">
        <Button
          className="h-12 w-full focus-ring"
          font="sans"
          size="lg"
          isLoading={isProcessing}
          onClick={onContinue}
        >
          Continue with SSO
        </Button>
        <button
          type="button"
          className="rounded-sm text-sm text-foreground-subtle focus-ring hover:text-foreground disabled:opacity-50"
          disabled={isProcessing}
          onClick={onUseCode}
        >
          Email me a code instead
        </button>
      </div>
    </div>
  </div>
);

type MeadowCodeStepProps = {
  email: string;
  code: string;
  setCode: (code: string) => void;
  onSubmit: (code: string) => void;
  onBack: () => void;
  refreshCode: () => void;
  isProcessing: boolean;
  errorCount?: number;
};

export const MeadowCodeStep = ({
  email,
  code,
  setCode,
  onSubmit,
  onBack,
  refreshCode,
  isProcessing,
  errorCount,
}: MeadowCodeStepProps) => {
  const sniperLink = getSniperLink(email);

  return (
    <div className="flex flex-1 flex-col">
      <MeadowBackButton onBack={onBack} disabled={isProcessing} />

      <div className="flex flex-1 flex-col justify-center">
        <MeadowHeader
          title="Verify your email"
          description={`Enter the code we sent to ${email}`}
        />

        {sniperLink && (
          <div className="mb-6 flex justify-center">
            <button
              type="button"
              onClick={() => {
                trackSniperLinkClicked(email, sniperLink.provider);
                handleExternalUrlClick(sniperLink.url);
              }}
              className={cn(ssoButtonClass, 'h-10 w-auto px-4')}
            >
              <Icons.gmail className="size-4" />
              <span>Open {sniperLink.label}</span>
              <Icons.externalLink2 className="size-3.5 opacity-60" />
            </button>
          </div>
        )}

        <div className="flex flex-col items-center gap-6">
          <OTPCodeInput
            value={code}
            onChange={(value) => {
              setCode(value);
              trackLoginCodeUpdateEvent(email, value);
            }}
            onComplete={(value) => {
              if (!isProcessing) {
                onSubmit(value);
              }
            }}
            disabled={isProcessing}
            shakeKey={errorCount}
          />

          <p className="text-center text-sm text-foreground-subtle">
            Didn&apos;t get it?{' '}
            <button
              type="button"
              className="cursor-pointer rounded-sm font-medium text-accent focus-ring disabled:cursor-not-allowed disabled:opacity-60"
              onClick={refreshCode}
              disabled={isProcessing}
            >
              Resend
            </button>
          </p>

          <Button
            className="h-12 w-full max-w-64 focus-ring"
            type="submit"
            size="lg"
            disabled={code.length !== 6 || isProcessing}
            onClick={() => onSubmit(code)}
            isLoading={isProcessing}
          >
            Verify
          </Button>
        </div>
      </div>
    </div>
  );
};

export type MeadowAuthFormProps = {
  showSsoForm: boolean;
  showCodeForm: boolean;
  // Email step
  email: string;
  setEmail: (email: string) => void;
  onEmailSubmit: () => void;
  onGoogleLogin: () => void;
  onAppleLogin?: () => void;
  onMicrosoftLogin?: () => void;
  onSsoContinue: () => void;
  onUseCode: () => void;
  // Code step
  code: string;
  setCode: (code: string) => void;
  onCodeSubmit: (code: string) => void;
  onBack: () => void;
  refreshCode: () => void;
  // Shared
  isProcessing: boolean;
  errorCount?: number;
  lastUsedEmail?: string | null;
  passwordStep?: ReactNode;
};

export const MeadowAuthForm = ({
  showSsoForm,
  showCodeForm,
  email,
  setEmail,
  onEmailSubmit,
  onGoogleLogin,
  onAppleLogin,
  onMicrosoftLogin,
  onSsoContinue,
  onUseCode,
  code,
  setCode,
  onCodeSubmit,
  onBack,
  refreshCode,
  isProcessing,
  errorCount,
  lastUsedEmail,
  passwordStep,
}: MeadowAuthFormProps) => {
  return (
    <MeadowScene>
      {passwordStep ? (
        passwordStep
      ) : showSsoForm ? (
        <MeadowSsoStep
          email={email}
          onContinue={onSsoContinue}
          onUseCode={onUseCode}
          onBack={onBack}
          isProcessing={isProcessing}
        />
      ) : !showCodeForm ? (
        <MeadowEmailStep
          email={email}
          setEmail={setEmail}
          onSubmit={onEmailSubmit}
          onGoogleLogin={onGoogleLogin}
          onAppleLogin={onAppleLogin}
          onMicrosoftLogin={onMicrosoftLogin}
          isProcessing={isProcessing}
          lastUsedEmail={lastUsedEmail}
        />
      ) : (
        <MeadowCodeStep
          email={email}
          code={code}
          setCode={setCode}
          onSubmit={onCodeSubmit}
          onBack={onBack}
          refreshCode={refreshCode}
          isProcessing={isProcessing}
          errorCount={errorCount}
        />
      )}
    </MeadowScene>
  );
};
